/** Shared, provider-free review contract. Also copied to the product frontend. */
export function isPlainFacebookPackage(manifest) {
  const files = manifest?.files || [];
  return manifest?.provider === 'facebook' && files.some(file => file.kind === 'media')
    && !files.some(file => /\.(xlsx|xls|json)$/i.test(file.name))
    && !(manifest.carousel_cards || []).length
    && !files.some(file => /(?:^|\/)card\s*\d+\.(png|jpe?g|webp)$/i.test(file.name))
    && !(manifest.instructions || []).some(entry => /^(?:Instagram|Facebook|Stories|Reels|Messenger|Audience Network)[^\n]*:\s*\d{3,4}x\d{3,4}/im.test(entry.text));
}

export const adSetAudience = group => {
  const suffix = String(group.ad_set_suffix || '').toLowerCase();
  const profiles = group.targeting_profiles || [];
  return (suffix.includes('site') && suffix.includes('rtg')) || profiles.some(key => String(key).includes('site_rtg')) ? 'site_rtg'
    : (suffix.includes('fb') && suffix.includes('rtg')) || profiles.some(key => String(key).includes('fb_rtg')) ? 'fb_rtg' : 'prospecting';
};

export function creativeReadiness(upload, review, template) {
  const result = { blocked: false, message: '', adCount: null };
  if (!upload) return result;
  if (upload.manifest?.provider === 'google-ads') {
    const groups = upload.manifest.search?.ad_groups;
    if (groups?.length) result.adCount = groups.reduce((count, group) => count + (group.ads?.length || 1), 0);
    return result;
  }
  const rebuild = message => ({ ...result, blocked: true, message: `${message} Edit inputs and rebuild the draft to review the ads before approval.` });
  if (!upload.manifest) return rebuild('This saved creative package has no verified file manifest. Upload the package again.');
  const keys = ['primary_text', 'headline', 'description', 'call_to_action'];
  if (keys.some(key => !String(review?.copy?.[key] || '').trim())) return rebuild('The saved draft is missing required ad copy.');
  if (isPlainFacebookPackage(upload.manifest) && !review?.media_files?.length) return rebuild('This saved draft has no verified mapping from assets to ads.');
  const groups = template?.ad_sets || [];
  if (template && !groups.length) return rebuild('This draft has no ad sets for its creative package.');
  if (review?.concepts) {
    if (!review.concepts.length || review.concepts.some(concept => !concept.media?.length || !concept.audiences?.length
        || keys.some(key => !String(concept.copy?.[key] || '').trim()))) return rebuild('The creative concepts are incomplete.');
    if (groups.length && (groups.some(group => !review.concepts.some(concept => concept.audiences.includes(adSetAudience(group))))
        || review.concepts.some(concept => !groups.some(group => concept.audiences.includes(adSetAudience(group)))))) return rebuild('Creative concepts do not match the planned audiences.');
    result.adCount = groups.length ? groups.reduce((count, group) => count + review.concepts.filter(concept => concept.audiences.includes(adSetAudience(group))).length, 0) : null;
  } else if (review?.verification_version === 1) {
    const ads = review.source_ads, source = upload.manifest.meta?.ads;
    if (!ads?.length || !source?.length || ads.length !== source.length
        || new Set(ads.map(ad => ad.asset_id)).size !== ads.length
        || ads.some((ad, index) => ad.asset_id !== source[index].asset_id
          || ad.source_file !== source[index].source_file
          || JSON.stringify(ad.media_files) !== JSON.stringify(source[index].media_files))) {
      return rebuild('This saved draft has no verified mapping from assets to ads.');
    }
    if (groups.length) result.adCount = ads.length * groups.length;
  } else if (review?.media_files?.length && groups.length) result.adCount = review.media_files.length * groups.length;
  return result;
}

export function metaTargetingReadiness(brief, template) {
  const setup = brief?.campaign_setup || {};
  const planned = template?.adpilot_targeting;
  const error = 'The draft has not verified your device and interest settings. Edit inputs and rebuild the draft before approval.';
  if (setup.meta_context_version === 1) {
    const context = template?.adpilot_context_targeting;
    const requested = { context: (brief.additional_context || '').trim(), geo: brief.geo || '',
      device: setup.meta_device ?? 'all', audience_mode: setup.meta_audience_mode ?? 'automatic',
      fb_rtg: setup.fb_rtg ?? true, site_rtg: setup.site_rtg ?? true };
    if (context?.version !== 1 || Object.entries(requested).some(([key, value]) => context.requested?.[key] !== value)
        || !context.resolved || planned?.version !== 1
        || !['all', 'iphone'].includes(context.resolved.device)
        || !['automatic', 'broad'].includes(context.resolved.audience_mode)
        || planned.device !== context.resolved.device || planned.audience_mode !== context.resolved.audience_mode
        || template.ai_campaign?.brief?.geo !== context.resolved.geo
        || template.tracking?.fb_rtg !== context.resolved.fb_rtg || template.tracking?.site_rtg !== context.resolved.site_rtg) return error;
    return '';
  }
  if (!('meta_device' in setup) && !('meta_audience_mode' in setup)) return '';
  if (!planned || planned.version !== 1 || planned.device !== setup.meta_device || planned.audience_mode !== setup.meta_audience_mode) return error;
  return '';
}
